/**
 * @module use-cases/app/slice
 * @desc App Slice (Reducers and ExtraReducers)
 *
 */
import { actions as actionsCart } from 'core/use-cases/cart';
import { actions as actionsshippingModel } from 'core/use-cases/shippingModel';
import { createSlice, isAnyOf } from 'core/use-cases/utilities';

import { getScreenSize, isMobile } from '../helpers';

import * as actions from './actions';
import { initialState } from './initial-state';

const appSlice = createSlice({
  name: 'app',
  initialState,
  reducers: {},

  extraReducers: (builder) => {
    builder.addCase(actions.setLoading, (state, { payload = '' }) => {
      state.loader.message = payload;
      state.loader.isLoading =
        payload === '' ? initialState.loader.isLoading : !state.loader.isLoading;
    });

    builder.addCase(actions.setLocation, (state, { payload }) => {
      if (payload === undefined) {
        return;
      }
      const { current = '', previous = '' } = payload;

      state.location.previous = previous;
      state.location.current = current;
    });

    builder.addCase(actions.setBreadcrumbs, (state, { payload = {} }) => {
      const { currentState = {}, homePage, routes = [] } = payload;
      const breadcrumbs = [];
      const previousPath = routes.find(
        (route: { path: string }) =>
          route.path === state.location.previous && route.path !== homePage,
      );
      const currentPath = routes.find(
        (route: { path: string }) => route.path === state.location.current,
      );

      if (previousPath?.path) {
        breadcrumbs.push({
          name: previousPath?.name || '',
          url: previousPath?.path || '',
        });
      }

      if (state.location.current !== homePage) {
        breadcrumbs.push({
          name: currentState?.pathTitle ?? currentPath?.name ?? '',
          url: state.location.current,
        });
      }

      state.breadcrumbs = breadcrumbs;
    });

    builder.addCase(actions.hideNotification, (state, { payload }) => {
      state.notifications = state.notifications.filter(
        (notification) => notification.id !== payload,
      );
    });

    builder.addCase(actions.showNotification, (state, { payload }) => {
      if (payload !== undefined) {
        state.notifications.push(payload);
      }
    });

    builder.addCase(actions.setScreenSize, (state) => {
      state.isMobile = isMobile();
      state.screenSize = getScreenSize();
    });

    builder.addCase(actions.toggleCart, (state, { payload }) => {
      if (payload === undefined) {
        return;
      }
      state.isCartActive = payload;
      state.isDrawerActive = payload;
      state.isMobileMenuActive = false;
      state.isScrollLocked = payload;
      state.isSearchActive = false;
    });

    builder.addCase(actions.toggleSearch, (state, { payload }) => {
      if (payload === undefined) {
        return;
      }
      state.isDrawerActive = payload;
      state.isScrollLocked = payload;
      state.isSearchActive = payload;
    });

    builder.addCase(actions.toggleMenu, (state, { payload }) => {
      if (payload === undefined) {
        return;
      }
      state.isCartActive = false;
      state.isDrawerActive = false;
      state.isMobileMenuActive = !state.isMobileMenuActive;
      state.isScrollLocked = payload;
      state.isSearchActive = false;
      state.isShippingActive = false;
    });

    builder.addCase(actions.toggleShipping, (state, { payload }) => {
      if (payload === undefined) {
        return;
      }
      state.isDrawerActive = payload;
      state.isMobileMenuActive = false;
      state.isProductNotFoundShippingActive = false;
      state.isScrollLocked = payload;
      state.isSearchActive = false;
      state.isShippingActive = payload;
    });

    builder.addCase(actions.toggleProductNotFoundShipping, (state, { payload }) => {
      if (payload === undefined) {
        return;
      }
      state.isDrawerActive = true;
      state.isProductNotFoundShippingActive = payload;
      state.isScrollLocked = true;
    });

    builder.addCase(actions.togglePointsProgram, (state, { payload }) => {
      if (payload === undefined) {
        return;
      }
      state.isDrawerActive = true;
      state.isScrollLocked = true;
      state.isCartActive = !payload;
      state.isPointsProgramActive = payload;
    });

    builder.addCase(actions.canLoadVWO, (state) => {
      state.canLoadVWO = true;
    });

    builder.addMatcher(
      isAnyOf(
        actionsCart.cartSetShippingModelFetch.success,
        actionsshippingModel.currentDeliveryModeFetch.success,
      ),
      (state) => {
        state.isShippingActive = false;
      },
    );
  },
});

export const { reducer } = appSlice;
export { actions };
